/**
 * Reusable Expense Invoice Form Component
 * Used by both CreateExpenseInvoicePage and EditExpenseInvoicePage
 */

import React from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  Divider,
  IconButton,
  Stack,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  expenseInvoiceSchema,
  ExpenseInvoiceFormData,
} from '../Validation/expenseInvoiceValidation';
import { useExpenseInvoiceCalculations } from '../Hooks/useExpenseInvoiceCalculations';

interface ExpenseInvoiceFormProps {
  defaultValues?: Partial<ExpenseInvoiceFormData>;
  onSubmit: (data: ExpenseInvoiceFormData) => void | Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  mode: 'create' | 'edit';
}

const CURRENCIES = ['PLN', 'EUR', 'USD', 'GBP', 'CHF'];

const VAT_RATES = [
  { label: '23%', value: 0.23 },
  { label: '8%', value: 0.08 },
  { label: '5%', value: 0.05 },
  { label: '0%', value: 0 },
  { label: 'ZW (Zwolnione)', value: 0 },
];

export const ExpenseInvoiceForm: React.FC<ExpenseInvoiceFormProps> = ({
  defaultValues,
  onSubmit,
  onCancel,
  isLoading = false,
  mode,
}) => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ExpenseInvoiceFormData>({
    resolver: yupResolver(expenseInvoiceSchema) as any,
    defaultValues: defaultValues || {
      invoiceNumber: '',
      issueDate: dayjs().format('YYYY-MM-DD'),
      saleDate: null,
      paymentDueDate: null,
      supplierName: '',
      supplierNip: null,
      supplierAddress: null,
      supplierBankAccount: null,
      currency: 'PLN',
      notes: null,
      items: [
        {
          name: '',
          quantity: 1,
          unitNet: 0,
          unitGross: 0,
          vatRate: 0.23,
          isVatExempt: false,
          vatExemptNote: null,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const watchItems = watch('items');
  const watchCurrency = watch('currency');
  const { totalNet, totalVat, totalGross, formatCurrency } =
    useExpenseInvoiceCalculations(watchItems);

  const handleFormSubmit = async (data: ExpenseInvoiceFormData) => {
    await onSubmit(data);
  };

  const handleAddItem = () => {
    append({
      name: '',
      quantity: 1,
      unitNet: 0,
      unitGross: 0,
      vatRate: 0.23,
      isVatExempt: false,
      vatExemptNote: null,
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
      <Box component="form" onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">
            {mode === 'create' ? 'Utwórz fakturę kosztową' : 'Edytuj fakturę kosztową'}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
              Anuluj
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              {isLoading ? 'Zapisywanie...' : 'Zapisz'}
            </Button>
          </Stack>
        </Stack>

        {/* Section 1: Basic Information */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Informacje podstawowe
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }} >
                <Controller
                  name="invoiceNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Numer faktury"
                      fullWidth
                      required
                      error={!!errors.invoiceNumber}
                      helperText={errors.invoiceNumber?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} >
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Waluta"
                      fullWidth
                      required
                      error={!!errors.currency}
                      helperText={errors.currency?.message}
                    >
                      {CURRENCIES.map((currency) => (
                        <MenuItem key={currency} value={currency}>
                          {currency}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }} >
                <Controller
                  name="issueDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Data wystawienia"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date) => field.onChange(date ? date.format('YYYY-MM-DD') : null)}
                      format="DD.MM.YYYY"
                      maxDate={dayjs()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: !!errors.issueDate,
                          helperText: errors.issueDate?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }} >
                <Controller
                  name="saleDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Data sprzedaży"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date) => field.onChange(date ? date.format('YYYY-MM-DD') : null)}
                      format="DD.MM.YYYY"
                      maxDate={dayjs()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.saleDate,
                          helperText: errors.saleDate?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }} >
                <Controller
                  name="paymentDueDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Termin płatności"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date) => field.onChange(date ? date.format('YYYY-MM-DD') : null)}
                      format="DD.MM.YYYY"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.paymentDueDate,
                          helperText: errors.paymentDueDate?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Section 2: Supplier Information */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Dane dostawcy
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }} >
                <Controller
                  name="supplierName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Nazwa dostawcy"
                      fullWidth
                      required
                      error={!!errors.supplierName}
                      helperText={errors.supplierName?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} >
                <Controller
                  name="supplierNip"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="NIP"
                      fullWidth
                      error={!!errors.supplierNip}
                      helperText={
                        errors.supplierNip?.message ||
                        (watchCurrency && watchCurrency !== 'PLN'
                          ? 'Dla dostawcy zagranicznego: format kraju dostawcy'
                          : '10 cyfr')
                      }
                      placeholder="1234567890"
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} >
                <Controller
                  name="supplierAddress"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Adres"
                      fullWidth
                      multiline
                      rows={2}
                      error={!!errors.supplierAddress}
                      helperText={errors.supplierAddress?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} >
                <Controller
                  name="supplierBankAccount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Konto bankowe"
                      fullWidth
                      error={!!errors.supplierBankAccount}
                      helperText={errors.supplierBankAccount?.message || 'Format IBAN'}
                      placeholder="PL12 3456 7890 1234 5678 9012 3456"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Section 3: Invoice Items */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Pozycje faktury</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                disabled={isLoading}
              >
                Dodaj pozycję
              </Button>
            </Stack>
            <Divider sx={{ mb: 3 }} />

            {errors.items && typeof errors.items.message === 'string' && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.items.message}
              </Alert>
            )}

            {fields.map((field, index) => (
              <Card key={field.id} variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1">Pozycja #{index + 1}</Typography>
                    {fields.length > 1 && (
                      <IconButton
                        color="error"
                        onClick={() => remove(index)}
                        disabled={isLoading}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }} >
                      <Controller
                        name={`items.${index}.name`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Nazwa pozycji"
                            fullWidth
                            required
                            error={!!errors.items?.[index]?.name}
                            helperText={errors.items?.[index]?.name?.message}
                          />
                        )}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }} >
                      <Controller
                        name={`items.${index}.quantity`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            label="Ilość"
                            fullWidth
                            required
                            inputProps={{ min: 0.01, step: 0.01 }}
                            error={!!errors.items?.[index]?.quantity}
                            helperText={errors.items?.[index]?.quantity?.message}
                          />
                        )}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }} >
                      <Controller
                        name={`items.${index}.unitNet`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            label="Cena netto"
                            fullWidth
                            required
                            inputProps={{ step: 0.01 }}
                            error={!!errors.items?.[index]?.unitNet}
                            helperText={errors.items?.[index]?.unitNet?.message}
                          />
                        )}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }} >
                      <Controller
                        name={`items.${index}.unitGross`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            label="Cena brutto"
                            fullWidth
                            required
                            inputProps={{ step: 0.01 }}
                            error={!!errors.items?.[index]?.unitGross}
                            helperText={errors.items?.[index]?.unitGross?.message}
                          />
                        )}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }} >
                      <Controller
                        name={`items.${index}.vatRate`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            select
                            label="Stawka VAT"
                            fullWidth
                            required
                            error={!!errors.items?.[index]?.vatRate}
                            helperText={errors.items?.[index]?.vatRate?.message}
                          >
                            {VAT_RATES.map((rate) => (
                              <MenuItem key={rate.value} value={rate.value}>
                                {rate.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }} >
                      <Controller
                        name={`items.${index}.isVatExempt`}
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Checkbox {...field} checked={field.value} />}
                            label="Zwolnione z VAT"
                          />
                        )}
                      />
                    </Grid>

                    {watch(`items.${index}.isVatExempt`) && (
                      <Grid size={{ xs: 12 }} >
                        <Controller
                          name={`items.${index}.vatExemptNote`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              value={field.value || ''}
                              label="Podstawa prawna zwolnienia"
                              fullWidth
                              required
                              multiline
                              rows={2}
                              error={!!errors.items?.[index]?.vatExemptNote}
                              helperText={errors.items?.[index]?.vatExemptNote?.message}
                              placeholder="np. Art. 43 ust. 1 pkt 1 ustawy o VAT"
                            />
                          )}
                        />
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>

        {/* Section 4: Summary */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Podsumowanie
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }} >
                <Typography variant="subtitle2" color="text.secondary">
                  Suma netto
                </Typography>
                <Typography variant="h6">{formatCurrency(totalNet, watchCurrency)}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }} >
                <Typography variant="subtitle2" color="text.secondary">
                  Suma VAT
                </Typography>
                <Typography variant="h6">{formatCurrency(totalVat, watchCurrency)}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }} >
                <Typography variant="subtitle2" color="text.secondary">
                  Suma brutto
                </Typography>
                <Typography variant="h5" color="primary" fontWeight="bold">
                  {formatCurrency(totalGross, watchCurrency)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Section 5: Notes */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Notatki (opcjonalnie)
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value || ''}
                  label="Dodatkowe informacje"
                  fullWidth
                  multiline
                  rows={4}
                  error={!!errors.notes}
                  helperText={errors.notes?.message || 'Maksymalnie 1000 znaków'}
                  placeholder="Dodatkowe uwagi dotyczące faktury..."
                />
              )}
            />
          </CardContent>
        </Card>

        {/* Actions (repeated at bottom for convenience) */}
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
            Anuluj
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? 'Zapisywanie...' : 'Zapisz'}
          </Button>
        </Stack>
      </Box>
    </LocalizationProvider>
  );
};
